import { useEffect, useContext, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, RefreshCcw, ArrowLeft, Send, CalendarDays } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const DutyCardEntryNew = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();
const dailyId = sessionStorage.getItem("DailyID");
  const finYearId = localStorage.getItem("finYearId") || "";
  const branchIdLS = localStorage.getItem("branchId") || "";
  const branchTypeLS = localStorage.getItem("branchType") || "HO";

  const [branches, setBranches] = useState([]);
  const [halfShiftAmounts, setHalfShiftAmounts] = useState([]);

  const [branch, setBranch] = useState("Select");
  const [date, setDate] = useState(""); // YYYY-MM-DD

  const [mode, setMode] = useState(null); // "insert" | "update"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // header “select all”
  const [headLeave, setHeadLeave] = useState(false);
  const [headDS1, setHeadDS1] = useState(false);
  const [headDS2, setHeadDS2] = useState(false);
  const [headNS1, setHeadNS1] = useState(false);
  const [headNS2, setHeadNS2] = useState(false);
  const [headHD, setHeadHD] = useState(false);
  const [headHN, setHeadHN] = useState(false);
  const [headSelectAll, setHeadSelectAll] = useState(false);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-17/1", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const miniInput =
    `w-[90px] px-3 py-2 rounded-xl border outline-none ` +
    (isDark ? "bg-gray-800/40 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900");

  const requireBasics = () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    if (!String(branchIdLS).trim() || !String(branchTypeLS).trim()) {
      toast.warn("Branch details missing. Please login again.");
      return false;
    }
    return true;
  };

  // Branch mapping helper (SP output differs)
  const getBranchValue = (b) =>
    b?.fld_Branchid ?? b?.fld_branchid ?? b?.branchid ?? b?.id ?? b?.Value ?? b?.value ?? b?.fld_BranchID ?? "";
  const getBranchText = (b) =>
    b?.fld_BranchName ?? b?.fld_branchname ?? b?.branchname ?? b?.name ?? b?.Text ?? b?.text ?? b?.fld_BranchName ?? "";

  /** ---- Core calculation logic ---- */
  const toNum = (x) => {
    const n = Number(x);
    return Number.isFinite(n) ? n : 0;
  };

  const computeRow = (r) => {
    if (r.leave) {
      return {
        ...r,
        day1: false,
        day2: false,
        night1: false,
        night2: false,
        halfday: false,
        halfnight: false,
        heat: false,
        ot: false,
        noofdays: "0",
        totalamount: "0",
        heatallowance: "0",
        noofheat: "0",
        othrs: "0",
        otamt: "0",
        cla: false,
        halfcla: false,
        clanoofdays: "0",
        claallowance: "0",
        netamount: "0",
        halfshiftamt: "0",
      };
    }

    let i = 0;
    let i1 = 0;
    let i2 = 0;

    if (r.day1) { i += 1; i1 += 1; }
    if (r.day2) { i += 1; i1 += 1; }
    if (r.night1) { i += 1; i1 += 1; }
    if (r.night2) { i += 1; i1 += 1; }
    if (r.halfday) { i += 0.5; i2 += 1; }
    if (r.halfnight) { i += 0.5; i2 += 1; }

    if (r.notpay) {
      i = i - i1;
      i1 = 0;
    }

    let hsAmt = "0";
    if (r.halfday || r.halfnight) hsAmt = String(r.halfshiftamt || "0");
    else hsAmt = "0";

    const heatallow = r.heat ? String(r.heatallowance || "0") : "0";
    const noheat = r.heat ? String(r.noofheat || "0") : "0";
    const otamt = r.ot ? String(r.otamt || "0") : "0";
    const othrs = r.ot ? String(r.othrs || "0") : "0";

    const baseCla = toNum(r.baseCla || 0);
    let allow = 0;
    let claNoDays = 0;

    let claFlag = !!r.cla;
    let halfClaFlag = !!r.halfcla;

    if (claFlag) {
      halfClaFlag = false;
      if (baseCla > 0) {
        allow = baseCla;
        claNoDays = 1;
      }
    } else if (halfClaFlag) {
      claFlag = false;
      if (baseCla > 0) {
        allow = baseCla / 2;
        claNoDays = 0.5;
      }
    }

    const rate = toNum(r.rate);
    const total = rate * i1 + toNum(hsAmt) * i2 + toNum(heatallow) + toNum(otamt);
    const net = rate * i1 + toNum(hsAmt) * i2 + allow + toNum(heatallow) + toNum(otamt);

    return {
      ...r,
      cla: claFlag,
      halfcla: halfClaFlag,
      noofdays: String(i),
      clanoofdays: String(claNoDays),
      claallowance: String(allow),
      halfshiftamt: String(hsAmt),
      heatallowance: String(heatallow),
      noofheat: String(noheat),
      otamt: String(otamt),
      othrs: String(othrs),
      totalamount: String(total),
      netamount: String(net),
    };
  };

  const setRow = (idx, patch) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = computeRow({ ...next[idx], ...patch });
      return next;
    });
  };

  /** ---- Fetchers ---- */
  const fetchFilters = useCallback(async () => {
    if (!requireBasics()) return;
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/duty-card-entry/filters`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchIdLS),
          branchType: String(branchTypeLS),
        },
      });

      if (res?.data?.success) {
        const data = res.data.rows || {};
        setBranches(data.branches || []);
        if (data.branchid) setBranch(String(data.branchid));
      } else {
        toast.error(res?.data?.message || "Failed to load branches");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }, [finYearId, branchIdLS, branchTypeLS]);

  const fetchHalfShift = useCallback(async () => {
    if (!requireBasics()) return;
    try {
      const res = await axios.get(`${BASE_URL}/api/duty-card-entry/halfshift-amounts`, {
        params: { finYearId: Number(finYearId) },
      });
      if (res?.data?.success) setHalfShiftAmounts(res.data.rows || []);
    } catch {}
  }, [finYearId]);

  const fetchClaAllowanceForRows = useCallback(
    async (gridRows) => {
      const ids = Array.from(new Set(gridRows.map((x) => Number(x.empid)).filter((x) => x > 0)));
      if (!ids.length) return gridRows;

      try {
        const res = await axios.post(`${BASE_URL}/api/duty-card-entry/cla-allowance`, {
          finYearId: Number(finYearId),
          employeeIds: ids,
        });

        if (res?.data?.success) {
          const map = new Map(
            (res.data.rows || []).map((x) => [Number(x.employeeId), Number(x.claAllowance || 0)])
          );
          return gridRows.map((r) => ({ ...r, baseCla: map.get(Number(r.empid)) ?? 0 }));
        }
      } catch {}
      return gridRows;
    },
    [finYearId]
  );

  const fetchGrid = useCallback(async () => {
    if (!requireBasics()) return;

    if (branch === "Select") {
      toast.warn("Please select Branch..");
      return;
    }
    if (!date) {
      toast.warn("Please select Date..");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/duty-card-entry/grid`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchIdLS),
          branchType: String(branchTypeLS),
          branch,
          date,
        },
      });

      if (!res?.data?.success) {
        toast.warn(res?.data?.message || "No Records Found");
        setRows([]);
        setMode(null);
        return;
      }

      const apiRows = res.data.rows || [];
      const newMode = res.data.mode;

      let normalized = apiRows.map((x) => ({
        selected: false,

        empid: x.fld_Employeeid ?? x.fld_employeeid ?? x.EmployeeId ?? 0,
        cardid: x.fld_cardid ?? 0,
        desigid: x.fld_desigid ?? 0,
        deptid: x.fld_deptid ?? 0,
        brnid: x.fld_branchid ?? 0,

        empname: x.fld_Employee ?? "",
        empcode: x.fld_Employeecode ?? "",
        designation: x.fld_designationname ?? "",

        dcardno: x.fld_dcardno ?? "",

        leave: !!x.fld_leave,
        day1: !!x.fld_dayshift1,
        day2: !!x.fld_dayshift2,
        night1: !!x.fld_nightshift1,
        night2: !!x.fld_nightshift2,
        halfday: !!x.fld_halfday,
        halfnight: !!x.fld_halfnight,

        notpay: !!x.fld_notpay,
        cla: !!x.fld_CLA,
        halfcla: !!x.fld_halfCLA,

        heat: !!x.fld_heat,
        noofheat: String(x.fld_noofheat ?? "0"),
        heatallowance: String(x.fld_heatallowance ?? "0"),

        ot: !!x.fld_ot,
        othrs: String(x.fld_othrs ?? "0"),
        otamt: String(x.fld_otamt ?? "0"),

        halfshiftamt: String(x.fld_halfshiftamt ?? "0"),

        noofdays: String(x.fld_noofdays ?? "0"),
        rate: String(x.fld_rate ?? "0"),
        totalamount: String(x.fld_totalamount ?? "0"),
        clanoofdays: String(x.fld_CLAnoofdays ?? "0"),
        claallowance: String(x.fld_CLAallowance ?? "0"),
        netamount: String(x.fld_Netamount ?? "0"),

        remark: x.fld_remark ?? "",
        baseCla: 0,
      }));

      normalized = await fetchClaAllowanceForRows(normalized);
      normalized = normalized.map((r) => computeRow(r));

      setMode(newMode);
      setRows(normalized);

      setHeadLeave(false);
      setHeadDS1(false);
      setHeadDS2(false);
      setHeadNS1(false);
      setHeadNS2(false);
      setHeadHD(false);
      setHeadHN(false);
      setHeadSelectAll(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }, [finYearId, branchIdLS, branchTypeLS, branch, date, fetchClaAllowanceForRows]);

  const fetchGridByDailyId = async (dailyId) => {
  try {
    setLoading(true);

    const res = await axios.get(`${BASE_URL}/api/duty-card-entry/by-dailyid`, {
      params: {
        finYearId: Number(finYearId),
        dailyId: Number(dailyId),
      },
    });

    if (!res?.data?.success) {
      toast.warn(res?.data?.message || "No Records Found");
      return;
    }

    const apiRows = res.data.rows || [];

    let normalized = apiRows.map((x) => ({
      selected: false,

      empid: x.fld_employeeid,
      cardid: x.fld_cardid,
      desigid: x.fld_desigid,
      deptid: x.fld_deptid,
      brnid: x.fld_branchid,

      empname: x.fld_employee,
      empcode: x.fld_employeecode,
      designation: x.fld_designationname,

      dcardno: x.fld_dcardno,

      leave: !!x.fld_leave,
      day1: !!x.fld_dayshift1,
      day2: !!x.fld_dayshift2,
      night1: !!x.fld_nightshift1,
      night2: !!x.fld_nightshift2,
      halfday: !!x.fld_halfday,
      halfnight: !!x.fld_halfnight,

      notpay: !!x.fld_notpay,
      cla: !!x.fld_CLA,
      halfcla: !!x.fld_halfCLA,

      heat: !!x.fld_heat,
      noofheat: String(x.fld_noofheat ?? "0"),
      heatallowance: String(x.fld_heatallowance ?? "0"),

      ot: !!x.fld_ot,
      othrs: String(x.fld_othrs ?? "0"),
      otamt: String(x.fld_otamt ?? "0"),

      halfshiftamt: String(x.fld_halfshiftamt ?? "0"),

      noofdays: String(x.fld_noofdays ?? "0"),
      rate: String(x.fld_rate ?? "0"),
      totalamount: String(x.fld_totalamount ?? "0"),
      clanoofdays: String(x.fld_CLAnoofdays ?? "0"),
      claallowance: String(x.fld_CLAallowance ?? "0"),
      netamount: String(x.fld_Netamount ?? "0"),

      remark: x.fld_remark ?? "",
      baseCla: 0,
    }));

    normalized = await fetchClaAllowanceForRows(normalized);
    normalized = normalized.map((r) => computeRow(r));

    // Auto set branch & date from DB (ASP behavior)
    setBranch(String(apiRows[0].fld_branchid));
    setDate(apiRows[0].fld_date?.substring(0, 10));

    setRows(normalized);
  } catch (err) {
    toast.error(err?.response?.data?.message || err?.message);
  } finally {
    setLoading(false);
  }
};


  /** ---- Mount ---- */
  useEffect(() => {
    fetchFilters();
    fetchHalfShift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ✅ ADD / EDIT / VIEW logic (FIXED PROPERLY)
   */
  const [readOnly, setReadOnly] = useState(false);
  const autoLoadedRef = useRef(false);


useEffect(() => {
  if (!requireBasics()) return;

  if (dailyId) {
    setMode("update");
    setReadOnly(location.state?.mode === "view"); // ✅ VIEW MODE
    fetchGridByDailyId(dailyId);
  } else {
    setMode("insert");
    setRows([]);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);





  /** ---- Header logic ---- */
  const applyAll = (field, value) => {
    setRows((prev) => prev.map((r) => computeRow({ ...r, [field]: value })));
  };

  const handleHeadLeave = (v) => {
    setHeadLeave(v);
    if (v) {
      setHeadDS1(false);
      setHeadDS2(false);
      setHeadNS1(false);
      setHeadNS2(false);
      setHeadHD(false);
      setHeadHN(false);
      applyAll("leave", true);
    } else {
      applyAll("leave", false);
    }
  };

  const handleSubmit = async () => {
    if (readOnly) {
      toast.warn("View mode: editing is disabled.");
      return;
    }

    if (!requireBasics()) return;
    if (branch === "Select") return toast.warn("Please select Branch..");
    if (!date) return toast.warn("Please select Date..");
    if (!rows.length) return toast.warn("No Records Found");

    const selected = rows.filter((r) => r.selected);
    if (!selected.length) return toast.warn("Select any one..");

    const ok = window.confirm("Are you sure to submit?");
    if (!ok) return;

    try {
      setLoading(true);

      const payload = {
        finYearId: Number(finYearId),
        mode,
        date,
        branch,
        rows: rows.map((r) => ({
          selected: !!r.selected,
          empid: r.empid,
          cardid: r.cardid,
          desigid: r.desigid,
          deptid: r.deptid,
          brnid: r.brnid,
          dcardno: r.dcardno,

          leave: r.leave,
          day1: r.day1,
          day2: r.day2,
          night1: r.night1,
          night2: r.night2,
          halfday: r.halfday,
          halfnight: r.halfnight,

          notpay: r.notpay,
          cla: r.cla,
          halfcla: r.halfcla,

          noofdays: r.noofdays,
          rate: r.rate,
          totalamount: r.totalamount,
          clanoofdays: r.clanoofdays,
          claallowance: r.claallowance,
          netamount: r.netamount,

          remark: r.remark,

          halfshiftamt: r.halfshiftamt,

          heat: r.heat,
          heatallowance: r.heatallowance,
          noofheat: r.noofheat,

          ot: r.ot,
          otamt: r.otamt,
          othrs: r.othrs,
        })),
      };

      const res = await axios.post(`${BASE_URL}/api/duty-card-entry/submit`, payload);

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Updated Successfully..");
        navigate("/payroll/dutyCardListing");
      } else {
        toast.warn(res?.data?.message || "Submit failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

const handleBack = () => {
  sessionStorage.removeItem("DailyID"); // ASP: Session.Remove
  navigate("/payroll/dutyCardListing");
};


  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DUTY CARD ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>Bata Entry</div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>Search by Branch + Date, then edit and submit selected rows</div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              {mode && (
                <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                  Mode: {mode.toUpperCase()}
                </div>
              )}

              {readOnly && (
                <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-amber-900/40 text-amber-100" : "bg-amber-100 text-amber-800"}`}>
                  VIEW ONLY
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleBack} className={ghostBtn} disabled={loading}>
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              <button onClick={fetchGrid}   disabled={!!dailyId} className={ghostBtn} >
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh Grid
              </button>

              <button onClick={handleSubmit} className={primaryBtn} disabled={loading || rows.length === 0 || readOnly}>
                <Send className="w-4 h-4" /> Submit Selected
              </button>
            </div>
          </div>

          {/* Entry panel */}
          <div className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className={labelBase}>
                  Branch <span className="text-red-500">*</span>
                </label>
<select
  value={branch}
  onChange={(e) => setBranch(e.target.value)}
  disabled={!!dailyId}
   className={inputBase}
>                  <option value="Select">Select</option>
                  {branches.map((b, i) => {
                    const v = getBranchValue(b);
                    const t = getBranchText(b);
                    return (
                      <option key={v || i} value={String(v)}>
                        {t || String(v)}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className={labelBase}>
                  Date <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <CalendarDays className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
<input
  type="date"
  value={date}
  onChange={(e) => setDate(e.target.value)}
  disabled={!!dailyId}
  className={inputBase}
/>
                </div>
              </div>

              <div className="flex items-end">
                <button onClick={fetchGrid} className={primaryBtn} disabled={!!dailyId}>
                  <Search className="w-4 h-4" /> Search
                </button>
              </div>
            </div>
          </div>

        <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-center">S.No</th>
                    <th className="px-3 py-3 text-left">Employee</th>
                    <th className="px-3 py-3 text-center">Emp Code</th>
                    <th className="px-3 py-3 text-left">Designation</th>
                    <th className="px-3 py-3 text-center">D.Card No</th>

                    <th className="px-3 py-3 text-center">
                      <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={headLeave} onChange={(e) => handleHeadLeave(e.target.checked)} disabled={readOnly} />
                        Leave
                      </label>
                    </th>

                    <th className="px-3 py-3 text-center">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={headDS1}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setHeadDS1(v);
                            applyAll("day1", v);
                          }}
                          disabled={readOnly}
                        />
                        Day1
                      </label>
                    </th>

                    <th className="px-3 py-3 text-center">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={headDS2}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setHeadDS2(v);
                            applyAll("day2", v);
                          }}
                          disabled={readOnly}
                        />
                        Day2
                      </label>
                    </th>

                    <th className="px-3 py-3 text-center">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={headNS1}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setHeadNS1(v);
                            applyAll("night1", v);
                          }}
                          disabled={readOnly}
                        />
                        Night1
                      </label>
                    </th>

                    <th className="px-3 py-3 text-center">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={headNS2}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setHeadNS2(v);
                            applyAll("night2", v);
                          }}
                          disabled={readOnly}
                        />
                        Night2
                      </label>
                    </th>

                    <th className="px-3 py-3 text-center">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={headHD}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setHeadHD(v);
                            applyAll("halfday", v);
                          }}
                          disabled={readOnly}
                        />
                        HalfDay
                      </label>
                    </th>

                    <th className="px-3 py-3 text-center">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={headHN}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setHeadHN(v);
                            applyAll("halfnight", v);
                          }}
                          disabled={readOnly}
                        />
                        HalfNight
                      </label>
                    </th>

                    <th className="px-3 py-3 text-center">N/P</th>
                    <th className="px-3 py-3 text-center">CLA</th>
                    <th className="px-3 py-3 text-center">HalfCLA</th>

                    <th className="px-3 py-3 text-center">Heat</th>
                    <th className="px-3 py-3 text-center">No Heat</th>
                    <th className="px-3 py-3 text-center">HeatAmt</th>

                    <th className="px-3 py-3 text-center">OT</th>
                    <th className="px-3 py-3 text-center">OT Hrs</th>
                    <th className="px-3 py-3 text-center">OTAmt</th>

                    <th className="px-3 py-3 text-center">HalfShift Amt</th>
                    <th className="px-3 py-3 text-center">No Days</th>
                    <th className="px-3 py-3 text-center">Rate</th>
                    <th className="px-3 py-3 text-center">Total</th>
                    <th className="px-3 py-3 text-center">CLA Days</th>
                    <th className="px-3 py-3 text-center">CLA Allow</th>
                    <th className="px-3 py-3 text-center">Net</th>
                    <th className="px-3 py-3 text-center">Remarks</th>

                    <th className="px-3 py-3 text-center">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={headSelectAll}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setHeadSelectAll(v);
                            setRows((prev) => prev.map((r) => ({ ...r, selected: v })));
                          }}
                          disabled={readOnly}
                        />
                        Select
                      </label>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 6 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td colSpan={30} className="px-4 py-6">
                          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                        </td>
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={30} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={`${r.empid}-${idx}`} className={isDark ? "bg-gray-900" : "bg-white"}>
                        <td className="px-3 py-3 text-center">{idx + 1}</td>
                        <td className="px-3 py-3">{r.empname}</td>
                        <td className="px-3 py-3 text-center">{r.empcode}</td>
                        <td className="px-3 py-3">{r.designation}</td>

                        <td className="px-3 py-3 text-center">
                          <input className={miniInput} value={r.dcardno} onChange={(e) => setRow(idx, { dcardno: e.target.value })} disabled={readOnly} />
                        </td>

                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.leave} onChange={(e) => setRow(idx, { leave: e.target.checked })} disabled={readOnly} />
                        </td>

                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.day1} onChange={(e) => setRow(idx, { day1: e.target.checked })} disabled={readOnly} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.day2} onChange={(e) => setRow(idx, { day2: e.target.checked })} disabled={readOnly} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.night1} onChange={(e) => setRow(idx, { night1: e.target.checked })} disabled={readOnly} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.night2} onChange={(e) => setRow(idx, { night2: e.target.checked })} disabled={readOnly} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.halfday} onChange={(e) => setRow(idx, { halfday: e.target.checked })} disabled={readOnly} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.halfnight} onChange={(e) => setRow(idx, { halfnight: e.target.checked })} disabled={readOnly} />
                        </td>

                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.notpay} onChange={(e) => setRow(idx, { notpay: e.target.checked })} disabled={readOnly} />
                        </td>

                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.cla} onChange={(e) => setRow(idx, { cla: e.target.checked })} disabled={readOnly} />
                        </td>

                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.halfcla} onChange={(e) => setRow(idx, { halfcla: e.target.checked })} disabled={readOnly} />
                        </td>

                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.heat} onChange={(e) => setRow(idx, { heat: e.target.checked })} disabled={readOnly} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input className={miniInput} disabled={!r.heat || readOnly} value={r.noofheat} onChange={(e) => setRow(idx, { noofheat: e.target.value })} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input className={miniInput} disabled={!r.heat || readOnly} value={r.heatallowance} onChange={(e) => setRow(idx, { heatallowance: e.target.value })} />
                        </td>

                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.ot} onChange={(e) => setRow(idx, { ot: e.target.checked })} disabled={readOnly} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input className={miniInput} disabled={!r.ot || readOnly} value={r.othrs} onChange={(e) => setRow(idx, { othrs: e.target.value })} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input className={miniInput} disabled={!r.ot || readOnly} value={r.otamt} onChange={(e) => setRow(idx, { otamt: e.target.value })} />
                        </td>

                        <td className="px-3 py-3 text-center">
                          <select
                            className={miniInput}
                            value={r.halfshiftamt}
                            onChange={(e) => setRow(idx, { halfshiftamt: e.target.value })}
                            disabled={!(r.halfday || r.halfnight) || readOnly}
                          >
                            <option value="0">0</option>
                            {halfShiftAmounts.map((h) => (
                              <option key={h.id} value={String(h.amount)}>
                                {h.amount}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="px-3 py-3 text-center">{r.noofdays}</td>
                        <td className="px-3 py-3 text-center">{r.rate}</td>
                        <td className="px-3 py-3 text-center">{Number(r.totalamount || 0).toFixed(2)}</td>
                        <td className="px-3 py-3 text-center">{r.clanoofdays}</td>
                        <td className="px-3 py-3 text-center">{Number(r.claallowance || 0).toFixed(2)}</td>
                        <td className="px-3 py-3 text-center">{Number(r.netamount || 0).toFixed(2)}</td>

                        <td className="px-3 py-3 text-center">
                          <input
                            className={`w-[140px] px-3 py-2 rounded-xl border outline-none ${
                              isDark ? "bg-gray-800/40 border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"
                            }`}
                            value={r.remark}
                            onChange={(e) => setRow(idx, { remark: e.target.value })}
                            disabled={readOnly}
                          />
                        </td>

                        <td className="px-3 py-3 text-center">
                          <input type="checkbox" checked={r.selected} onChange={(e) => setRow(idx, { selected: e.target.checked })} disabled={readOnly} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {rows.length > 0 && !loading && (
                  <tfoot>
                    <tr className={isDark ? "bg-gray-900" : "bg-white"}>
                      <td colSpan={29} className="px-4 py-4 text-right font-extrabold">
                        Submit Selected Rows
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button onClick={handleSubmit} className={primaryBtn} disabled={loading || readOnly}>
                          <Send className="w-4 h-4" /> Submit
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default DutyCardEntryNew;
